import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Form,
  Input,
  Space,
  Switch,
  Modal,
  message,
  Card,
  Tag,
  Row,
  Col,
} from 'antd';
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import api from '../../../api/axios';

const AddWarehouse = () => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [messageApi,contextHolder] = message.useMessage();
  // ====================== FETCH WAREHOUSES ======================
  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/warehouses');
      setWarehouses(res.data);
    } catch (err) {
      messageApi.error('Failed to fetch warehouses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  // ====================== ADD WAREHOUSE ======================
  const handleAddWarehouse = async (values) => {
    try {
      const warehouseType = values.isImport ? 'import' : 'retail';
      await api.post('/api/warehouses', {
        name: values.name,
        phone: values.phone,
        address: values.address,
        warehouseType,
      });
      messageApi.success('✅ Warehouse added successfully!');
      form.resetFields();
      fetchWarehouses();
    } catch (error) {
      console.error(error);
      messageApi.error(error.response?.data?.error || 'Failed to add warehouse');
    }
  };

  // ====================== EDIT WAREHOUSE ======================
  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setIsEditModalVisible(true);
    editForm.setFieldsValue({
      name: warehouse.name,
      phone: warehouse.phone,
      address: warehouse.address,
      isImport: warehouse.warehouseType === 'import',
    });
  };

  const handleEditSubmit = async (values) => {
    try {
      const warehouseType = values.isImport ? 'import' : 'retail';
      await api.put(`/api/warehouses/${editingWarehouse.id}`, {
        name: values.name,
        phone: values.phone,
        address: values.address,
        warehouseType,
      });
      messageApi.success('✅ Warehouse updated successfully!');
      setIsEditModalVisible(false);
      fetchWarehouses();
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update warehouse');
    }
  };

  // ====================== TOGGLE ACTIVE ======================
  const toggleActive = async (id) => {
    try {
      await api.patch(`/api/warehouses/${id}/toggle`);
      messageApi.success('Warehouse status updated');
      fetchWarehouses();
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to update warehouse status');
    }
  };

  // ====================== TOGGLE TYPE ======================
  const toggleType = async (id) => {
    try {
      await api.patch(`/api/warehouses/${id}/toggle-type`);
      messageApi.success('Warehouse type changed');
      fetchWarehouses();
    } catch (error) {
      console.error(error);
      messageApi.error('Failed to change warehouse type');
    }
  };

  // ====================== TABLE COLUMNS ======================
  const columns = [
    {
      title: 'Warehouse Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      responsive: ['md'],
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      responsive: ['md'],
    },
    {
      title: 'Type',
      dataIndex: 'warehouseType',
      key: 'warehouseType',
      render: (type) =>
        type === 'import' ? (
          <Tag color="blue">Import</Tag>
        ) : (
          <Tag color="green">Retail</Tag>
        ),
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (_, record) => (
        <Switch
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          checked={record.isActive}
          onChange={() => toggleActive(record.id)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            type="link"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            icon={<SwapOutlined />}
            type="link"
            onClick={() => toggleType(record.id)}
          >
            Change Type
          </Button>
        </Space>
      ),
    },
  ];

  // ====================== RENDER ======================
  return (
    <div>
      {contextHolder}
      <Card
        title="Add New Warehouse"
        bordered={false}
        style={{
          marginBottom: 24,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        }}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchWarehouses}
            loading={loading}
          >
            Refresh
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddWarehouse}
          initialValues={{ isImport: false }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="name"
                label="Warehouse Name"
                rules={[{ required: true, message: 'Please enter warehouse name' }]}
              >
                <Input placeholder="Warehouse Name" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input placeholder="Phone Number" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Please enter address' }]}
              >
                <Input placeholder="Address" />
              </Form.Item>
            </Col>

            <Col xs={12} sm={6} md={4}>
              <Form.Item
                name="isImport"
                label="Warehouse Type"
                valuePropName="checked"
              >
                <Switch checkedChildren="Import" unCheckedChildren="Retail" />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                block
              >
                Add Warehouse
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Warehouse List */}
      <Card
        title="Warehouses List"
        bordered={false}
        style={{
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 8,
        }}
      >
        <Table
          columns={columns}
          dataSource={warehouses}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 6 }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Warehouse"
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
      >
        <Form layout="vertical" form={editForm} onFinish={handleEditSubmit}>
          <Form.Item
            name="name"
            label="Warehouse Name"
            rules={[{ required: true, message: 'Please enter warehouse name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter phone number' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Please enter address' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="isImport"
            label="Warehouse Type"
            valuePropName="checked"
          >
            <Switch checkedChildren="Import" unCheckedChildren="Retail" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Update Warehouse
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AddWarehouse;
